import React, { useEffect, useState } from 'react'
import PreLoader from '../../../../core/common/preLoader';
import useFetch from '../../../../hooks/useFetch';
import { Link, useSearchParams } from 'react-router';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header/collapse-header';
import { Table, Tooltip, Badge, Tag, Card, Typography, Space, Alert } from 'antd';
import { ColumnProps } from 'antd/es/table';
import ModifyPeriod from './modifyPeriod';
import { Employee, Period as DataModelPeriod } from '../../../../types/dataModel';
import { getAPIHeaders } from '../../../../common/Utils';
import { API } from '../../../../common/Constants';
import DeleteModal from '../../../../core/modals/deleteModal';
import CopyRightInfo from '../../../../core/common/copyRightInfo';
import { useAccess } from '../../../../hooks/accessContext';
import dayjs from 'dayjs';
import './timetable.css';

// Defining our own Period interface for use in this component
interface Period extends Omit<DataModelPeriod, 'day'> {
  id?: number;
  day: string;
  batchId?: number;
  period: number;
  courseSubjectId?: number;
  subjectTitle?: string;
  facultyName?: string;
  facultyId?: number;
  startTime?: string;
  endTime?: string;
}

const { Title, Text } = Typography;

interface TimetableWeekPeriod {
  period: number,
  startTime?: string,
  endTime?: string,
  MONDAY?: Period,
  TUESDAY?: Period,
  WEDNESDAY?: Period,
  THURSDAY?: Period,
  FRIDAY?: Period,
  SATURDAY?: Period,
  SUNDAY?: Period
}

// Subject color mapping
const subjectColors: { [key: string]: string } = {};
const colorOptions = [
  '#108ee9', '#f50', '#87d068', '#2db7f5', '#ff85c0', '#7265e6', '#ffbf00',
  '#00a2ae', '#9254de', '#ff4d4f', '#52c41a', '#faad14', '#722ed1', '#13c2c2'
];

const Timetable = () => {
  const { hasClassMgntDelete, hasClassMgntEdit } = useAccess()
  const [searchParams] = useSearchParams();
  const { isLoading, error, callFetch } = useFetch();
  const [classID, setClassID] = useState<number>();
  const [curriculumCourseID, setCurriculumCourseID] = useState<number>();
  const [periods, setPeriods] = useState<TimetableWeekPeriod[]>([]);
  const [newPeriod, setNewPeriod] = useState<Period>();
  const [updatePeriod, setUpdatePeriod] = useState<Period>(); // New state for update
  const [employees, setEmployees] = useState<Employee[]>();
  const [subjects, setSubjects] = useState<any[]>();
  const [currentDay, setCurrentDay] = useState<string>(dayjs().format('dddd').toUpperCase());
  const [batchDetails, setBatchDetails] = useState<any>(null);

  const [deleteItem, setDeleteItem] = useState<any>({
    id: undefined,
    message: undefined,
  })

  useEffect(() => {
    fetchEmployees()
    setCurrentDay(dayjs().format('dddd').toUpperCase());
  }, [])

  useEffect(() => {
    if (curriculumCourseID) {
      fetchCurriculum360()
    }
  }, [curriculumCourseID])

  useEffect(() => {
    if (classID) {
      fetch(classID)
      fetchBatchDetails(classID)
    }
  }, [classID])

  useEffect(() => {
    let id = searchParams.get("id")
    let curriculumCourseID = searchParams.get("curriculumCourseId")
    if (id) {
      setClassID(parseInt(id))
    }
    if (curriculumCourseID) {
      setCurriculumCourseID(parseInt(curriculumCourseID))
    }
  }, [searchParams]);

  const fetch = (id: any) => {
    callFetch(`${API.TIMETABLE}/${id}`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      if (res && Array.isArray(res)) {
        // Fix for the Set spread operator issue
        const subjectSet = new Set<string>();
        res.filter(p => p.subjectTitle).forEach(p => subjectSet.add(p.subjectTitle));
        const uniqueSubjects = Array.from(subjectSet);
        
        uniqueSubjects.forEach((subject, index) => {
          if (subject && !subjectColors[subject]) {
            subjectColors[subject] = colorOptions[index % colorOptions.length];
          }
        });
      }
      setPeriods(parseToPeriods(res))
    });
  }

  const fetchBatchDetails = (id: any) => {
    // @ts-ignore - Using the BATCH property we defined above
    callFetch(`${API.CLASS}/${id}`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setBatchDetails(res)
    });
  }

  const deletePeriod = (id: any) => {
    callFetch(`${API.TIMETABLE}/${id}`,
      'DELETE',
      getAPIHeaders(),
      null,
      null
    ).then(() => {
      fetch(classID)
    });
  }

  const fetchEmployees = () => {
    callFetch(`${API.EMPLOYEE}`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setEmployees(parseEmployees(res));
    });
  }

  const fetchCurriculum360 = () => {
    callFetch(API.CURRICULUM_360,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      setSubjects(parseToSubjects(res, curriculumCourseID))
    });
  }

  const getSubjectColor = (subjectTitle: string | undefined) => {
    return subjectTitle && subjectColors[subjectTitle] ? subjectColors[subjectTitle] : '#1677ff';
  }

  const renderCell = (period: Period, day: string) => {
    const isCurrentDay = day === currentDay;
    const currentHour = dayjs().hour();
    const isPeriodNow = period.startTime && period.endTime &&
      currentHour >= parseInt(period.startTime.split(':')[0]) &&
      currentHour <= parseInt(period.endTime.split(':')[0]);

    const cellClassName =
      isCurrentDay ? 'current-day-cell' : '';

    return (
      period?.courseSubjectId
        ?
        <div className={`timetable-cell ${cellClassName} ${isCurrentDay && isPeriodNow ? 'current-period' : ''}`}>
          <Card
            size="small"
            className="subject-card"
            bordered={true}
            style={{
              backgroundColor: `${getSubjectColor(period.subjectTitle)}15`,
              borderLeft: `4px solid ${getSubjectColor(period.subjectTitle)}`,
            }}
          >
            {/* Subject Header with Action Buttons */}
            <div className="subject-card-header d-flex justify-content-between align-items-start mb-2">
              <Title level={5} style={{ margin: 0, color: getSubjectColor(period.subjectTitle) }}>
                {period.subjectTitle}
              </Title>
              <div className="action-buttons d-flex">
                {hasClassMgntEdit() && (
                  <Tooltip title="Update Period">
                    <button
                      className='btn btn-xs btn-info-light p-1 me-1'
                      data-bs-toggle="modal"
                      data-bs-target="#update_period"
                      onClick={() => setUpdatePeriod(period)}
                    >
                      <i className="ti ti-edit" />
                    </button>
                  </Tooltip>
                )}
                {hasClassMgntDelete() && (
                  <Tooltip title="Delete Period">
                    <button
                      className='btn btn-xs btn-danger-light p-1'
                      data-bs-toggle="modal"
                      data-bs-target="#delete_modal"
                      onClick={() => setDeleteItem({
                        id: period.id,
                        message: `Are you sure you want to delete Period ${period.period} for "${period.subjectTitle}" on ${period.day}? This can't be undone.`
                      })}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
            
            {/* Card Divider */}
            <div className="card-divider" style={{ 
              borderTop: '1px solid rgba(0,0,0,0.06)', 
              margin: '4px 0px 8px' 
            }}></div>
            
            {/* Subject Details */}
            <div className="subject-details">
              {/* Faculty Information */}
              <div className="faculty-info d-flex align-items-center mb-1">
                <i className="ti ti-user me-2" style={{ color: '#8c8c8c', fontSize: '14px' }}></i>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {period.facultyName}
                </Text>
              </div>
              
              {/* Time Information */}
              {period.startTime && period.endTime && (
                <div className="time-info d-flex align-items-center">
                  <i className="ti ti-clock me-2" style={{ color: '#8c8c8c', fontSize: '14px' }}></i>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatToHHMM(period.startTime)} - {formatToHHMM(period.endTime)}
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </div>
        :
        <div className={`timetable-cell empty-cell ${cellClassName}`}>
          <Tooltip title="Add subject for this period">
            <button
              disabled={!hasClassMgntEdit()}
              className="btn btn-sm btn-light btn-wave waves-effect waves-light"
              data-bs-toggle="modal"
              data-bs-target="#modify_period"
              onClick={() => setNewPeriod(period)}
            >
              <i className="ti ti-plus me-1"></i> Add
            </button>
          </Tooltip>
        </div>
    )
  }

  const columns: ColumnProps[] = [
    {
      title: () => (
        <div className="period-header">
          <Text strong>Period</Text>
        </div>
      ),
      dataIndex: "period",
      render: (text: String, record: any) => (
        <div className="period-cell">
          <Badge count={text} style={{ backgroundColor: '#1677ff', marginBottom: '3px' }} />
        </div>
      ),
      key: "period",
      width: 100,
      fixed: 'left'
    },
    {
      title: () => <div className={`day-header ${currentDay === 'MONDAY' ? 'current-day' : ''}`}>Monday</div>,
      render: (text: String, record: any) => (
        renderCell(record.MONDAY, 'MONDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'TUESDAY' ? 'current-day' : ''}`}>Tuesday</div>,
      render: (text: String, record: any) => (
        renderCell(record.TUESDAY, 'TUESDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'WEDNESDAY' ? 'current-day' : ''}`}>Wednesday</div>,
      render: (text: String, record: any) => (
        renderCell(record.WEDNESDAY, 'WEDNESDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'THURSDAY' ? 'current-day' : ''}`}>Thursday</div>,
      render: (text: String, record: any) => (
        renderCell(record.THURSDAY, 'THURSDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'FRIDAY' ? 'current-day' : ''}`}>Friday</div>,
      render: (text: String, record: any) => (
        renderCell(record.FRIDAY, 'FRIDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'SATURDAY' ? 'current-day' : ''}`}>Saturday</div>,
      render: (text: String, record: any) => (
        renderCell(record.SATURDAY, 'SATURDAY')
      ),
      width: 200
    },
    {
      title: () => <div className={`day-header ${currentDay === 'SUNDAY' ? 'current-day' : ''}`}>Sunday</div>,
      render: (text: String, record: any) => (
        renderCell(record.SUNDAY, 'SUNDAY')
      ),
      width: 200
    },
  ]

  const onAddPeriod = () => {
    let newPeriod = periods.length + 1;
    // Calculate default time for new period based on last period
    const lastPeriod = periods.length > 0 ? periods[periods.length - 1] : null;
    const defaultStartTime = lastPeriod && lastPeriod.endTime ? lastPeriod.endTime : "09:00";
    const defaultEndTime = calculateEndTime(defaultStartTime);
    
    setPeriods([...periods,
    {
      period: newPeriod,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      MONDAY: {
        day: 'MONDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      TUESDAY: {
        day: 'TUESDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      WEDNESDAY: {
        day: 'WEDNESDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      THURSDAY: {
        day: 'THURSDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      FRIDAY: {
        day: 'FRIDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      SATURDAY: {
        day: 'SATURDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      },
      SUNDAY: {
        day: 'SUNDAY',
        batchId: classID,
        period: newPeriod,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      }
    }])
  }

  // Calculate end time based on start time (default: 1 hour later)
  const calculateEndTime = (startTime: string): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    let endHour = hours + 1;
    // Handle day overflow
    if (endHour >= 24) {
      endHour = endHour - 24;
    }
    return `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Format any time string to HH:MM format
  const formatToHHMM = (time: string | undefined): string => {
    if (!time) return "00:00";
    
    // If time includes seconds (HH:MM:SS format), strip them out
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    
    // If it's already in HH:MM format, ensure proper padding
    if (/^\d{1,2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    
    // If it's another format, convert to HH:MM
    try {
      const timeObj = dayjs(time);
      if (timeObj.isValid()) {
        return timeObj.format('HH:mm');
      }
    } catch (e) {
      console.error('Error formatting time:', e);
    }
    
    return time;
  }

  function parseToPeriods(data: any[]) {
    let prds: TimetableWeekPeriod[] = []
    if (data) {
      data?.forEach((ele) => {
        let prd = prds.find((p) => p.period === parseInt(ele.period))
        if (!prd) {
          prd = {
            period: ele.period,
            startTime: ele.startTime || "",
            endTime: ele.endTime || "",
            MONDAY: {
              day: 'MONDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            TUESDAY: {
              day: 'TUESDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            WEDNESDAY: {
              day: 'WEDNESDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            THURSDAY: {
              day: 'THURSDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            FRIDAY: {
              day: 'FRIDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            SATURDAY: {
              day: 'SATURDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            },
            SUNDAY: {
              day: 'SUNDAY',
              batchId: classID,
              period: ele.period,
              startTime: ele.startTime || "",
              endTime: ele.endTime || "",
            }
          }
          prds.push(prd)
        }
        if (ele.day === 'MONDAY') {
          prd.MONDAY = {
            ...ele
          }
        } else if (ele.day === 'TUESDAY') {
          prd.TUESDAY = {
            ...ele
          }
        } else if (ele.day === 'WEDNESDAY') {
          prd.WEDNESDAY = {
            ...ele
          }
        } else if (ele.day === 'THURSDAY') {
          prd.THURSDAY = {
            ...ele
          }
        } else if (ele.day === 'FRIDAY') {
          prd.FRIDAY = {
            ...ele
          }
        } else if (ele.day === 'SATURDAY') {
          prd.SATURDAY = {
            ...ele
          }
        } else if (ele.day === 'SUNDAY') {
          prd.SUNDAY = {
            ...ele
          }
        }
      })
    }
    let maxPeriod = 0;
    data?.forEach((ele) => {
      if (ele.period > maxPeriod) {
        maxPeriod = ele.period
      }
    })

    for (let i = 1; i <= maxPeriod; i++) {
      let existing = prds.find((p) => p.period === i)
      if (!existing) {
        prds.push({
          period: i,
          startTime: `${8 + i}:00`,
          endTime: `${9 + i}:00`,
          MONDAY: {
            day: 'MONDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          TUESDAY: {
            day: 'TUESDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          WEDNESDAY: {
            day: 'WEDNESDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          THURSDAY: {
            day: 'THURSDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          FRIDAY: {
            day: 'FRIDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          SATURDAY: {
            day: 'SATURDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          },
          SUNDAY: {
            day: 'SUNDAY',
            batchId: classID,
            period: i,
            startTime: `${8 + i}:00`,
            endTime: `${9 + i}:00`,
          }
        })
      }
    }
    prds.sort((a, b) => a.period - b.period)
    return (prds)
  }

  const renderModifyPeriod = () => {
    // @ts-ignore - Ignoring type mismatch between our Period and dataModel.Period
    return <ModifyPeriod period={newPeriod} onSuccess={() => fetch(classID)} subjects={subjects || []} employees={employees || []} />
  }

  const renderUpdatePeriod = () => {
    // Convert the day string to the expected union type
    const adaptedPeriod = updatePeriod ? {
      ...updatePeriod,
      // Cast the day string to the appropriate type for the data model
      day: updatePeriod.day as any
    } : undefined;

    return <ModifyPeriod 
      period={adaptedPeriod} 
      onSuccess={() => fetch(classID)} 
      subjects={subjects || []} 
      employees={employees || []} 
      isUpdate={true} 
    />
  }

  const renderSubjectLegend = () => {
    const uniqueSubjects = Object.keys(subjectColors);
    if (uniqueSubjects.length === 0) return null;

    return (
      <Card className="subject-legend-card mb-3" title="Subject Legend" size="small">
        <div className="subject-legend">
          {uniqueSubjects.map((subject, index) => (
            <Tag key={index} color={subjectColors[subject]} style={{ margin: '4px' }}>
              {subject}
            </Tag>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <DeleteModal
        heading="Confirm Delete"
        message={deleteItem.message}
        onDelete={() => deletePeriod(deleteItem?.id)}
      />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Timetable: <span className='text-gray'>{batchDetails?.name} {batchDetails?.section}</span></h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.class}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Learning Center</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    <Link to={all_routes?.class}>
                      Classes
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Timetable
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <Link to={all_routes?.class} className="btn btn-outline-primary me-2">
                <i className="ti ti-arrow-left me-1"></i> Back to Classes
              </Link>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          
          {/* Batch Info Card */}
          {/* {batchDetails && (
            <Card className="mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div>
                  <Title level={4} style={{ margin: 0 }}>{batchDetails.name}</Title>
                  <Text type="secondary">Curriculum: {batchDetails.curriculumName}</Text>
                </div>
                <Space>
                  <Tag color="blue">Status: {batchDetails.status}</Tag>
                  <Tag color="green">Students: {batchDetails.noOfStudents || 0}</Tag>
                  {batchDetails.startDate && (
                    <Tag color="orange">
                      Duration: {dayjs(batchDetails.startDate).format('DD/MM/YYYY')} - {dayjs(batchDetails.endDate).format('DD/MM/YYYY')}
                    </Tag>
                  )}
                </Space>
              </div>
            </Card>
          )} */}

          {/* Today's highlight */}
          <Alert
            type="info"
            showIcon
            message={`Today is ${dayjs().format('dddd, MMMM D, YYYY')}`}
            description="Current day's schedule is highlighted in the timetable below."
            className="mb-3"
          />

          {/* Legend for subjects */}
          {renderSubjectLegend()}

          {/* Timetable Card */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Weekly Schedule of Batch {batchDetails?.name} {batchDetails?.section}</h5>
              <button
                disabled={!hasClassMgntEdit()}
                className="btn btn-primary d-flex align-items-center"
                onClick={onAddPeriod}
              >
                <i className="ti ti-circle-plus me-2" />
                Add Period
              </button>
            </div>
            <div className="card-body timetable-container">
              <Table
                className={"timetable-table"}
                columns={columns}
                dataSource={periods}
                pagination={false}
                bordered
                scroll={{ x: 'max-content', y: 500 }}
                sticky
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo />
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Period */}
      <div className="modal fade" id="modify_period">
        {renderModifyPeriod()}
      </div>
      {/* /Add Period */}
      {/* Update Period */}
      <div className="modal fade" id="update_period">
        {renderUpdatePeriod()}
      </div>
      {/* /Update Period */}
    </>
  )
}

function parseToSubjects(currs: any[], curriculumCourseID?: number) {
  let subjects: any[] = []
  if (currs) {
    currs.forEach((item) => {
      if (item && !subjects.some((el) => el.curriculumCourseId === item.curriculumCourseId)) {
        if (curriculumCourseID && parseInt(item.curriculumCourseId) === curriculumCourseID) {
          subjects.push({
            label: item.subjectTitle,
            value: item.courseSubjectId
          })
        } else if (!curriculumCourseID) {
          subjects.push({
            label: item.subjectTitle,
            value: item.courseSubjectId
          })
        }
      }
    })
  }
  return subjects;
}

function parseEmployees(emps: any) {
  let empOpts: any[] = [];

  if (emps && Array.isArray(emps.employeeProfiles)) {
    emps.employeeProfiles?.filter((emp: Employee) => emp.roles.includes('FACULTY')).forEach((emp: Employee) => {
      empOpts.push({
        label: `${emp.firstName} - ${emp.lastName} (${emp.departmentName} - ${emp.designationName})`,
        value: emp.employeeId,
      });
    });
  }

  return empOpts;
}

export default Timetable